import apiClient from './client';

export interface UploadMultipartConfig {
  thresholdMb: number;
  thresholdBytes: number;
  chunkSizeMb: number;
  chunkSizeBytes: number;
  maxConcurrency: number;
  urlExpiresSeconds: number;
}

export interface ChunkRange {
  partNumber: number;
  start: number;
  end: number;
}

type UploadBusinessType = 'project_resource' | 'attachment' | 'storyboard_image';

interface MultipartInitiateResponse {
  sessionId: string;
  uploadId: string;
  objectKey: string;
  partSize: number;
  expiresAt: string;
}

interface MultipartPartUrlResponse {
  parts: Array<{
    partNumber: number;
    uploadUrl: string;
  }>;
  expiresIn: number;
}

interface MultipartCompleteResponse<T> {
  sessionId: string;
  businessType: string;
  result: T;
}

export interface ChunkedUploadOptions {
  file: File;
  businessType: UploadBusinessType;
  businessPayload: Record<string, unknown>;
  multipartConfig?: UploadMultipartConfig;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

const DEFAULT_UPLOAD_MULTIPART_CONFIG: UploadMultipartConfig = {
  thresholdMb: 10,
  thresholdBytes: 10 * 1024 * 1024,
  chunkSizeMb: 5,
  chunkSizeBytes: 5 * 1024 * 1024,
  maxConcurrency: 3,
  urlExpiresSeconds: 3600,
};

const PART_URL_BATCH_SIZE = 1000;
const COMPLETE_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

export function shouldUseChunkedUpload(
  file: Pick<File, 'size'>,
  config: UploadMultipartConfig
): boolean {
  return file.size > config.thresholdBytes;
}

export function createChunkRanges(fileSize: number, chunkSize: number): ChunkRange[] {
  const ranges: ChunkRange[] = [];
  for (let start = 0, partNumber = 1; start < fileSize; start += chunkSize, partNumber += 1) {
    ranges.push({
      partNumber,
      start,
      end: Math.min(start + chunkSize, fileSize),
    });
  }
  return ranges;
}

export function calculateAggregateProgress(
  partProgress: Map<number, number>,
  totalBytes: number
): number {
  if (totalBytes <= 0) return 0;
  const loadedBytes = Array.from(partProgress.values()).reduce((sum, loaded) => sum + loaded, 0);
  return Math.min(100, Math.max(0, Math.round((loadedBytes * 100) / totalBytes)));
}

export function calculateUploadProgressPercent(loaded: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(99, Math.max(0, Math.round((loaded * 100) / total)));
}

export function recordPartProgress(
  partProgress: Map<number, number>,
  range: ChunkRange,
  loaded: number
): void {
  const partSize = range.end - range.start;
  const nextLoaded = Math.min(partSize, Math.max(0, loaded));
  const previousLoaded = partProgress.get(range.partNumber) ?? 0;
  partProgress.set(range.partNumber, Math.max(previousLoaded, nextLoaded));
}

export function chunkPartNumbers(
  partNumbers: number[],
  batchSize = PART_URL_BATCH_SIZE
): number[][] {
  const normalizedBatchSize = Math.max(1, Math.floor(batchSize));
  const batches: number[][] = [];
  for (let start = 0; start < partNumbers.length; start += normalizedBatchSize) {
    batches.push(partNumbers.slice(start, start + normalizedBatchSize));
  }
  return batches;
}

export async function getUploadMultipartConfig(): Promise<UploadMultipartConfig> {
  try {
    return (await apiClient.get('/uploads/config')) as UploadMultipartConfig;
  } catch {
    return DEFAULT_UPLOAD_MULTIPART_CONFIG;
  }
}

export async function uploadFileInChunks<T>(options: ChunkedUploadOptions): Promise<T> {
  const config = options.multipartConfig ?? (await getUploadMultipartConfig());
  const initiate = (await apiClient.post('/uploads/multipart/initiate', {
    fileName: options.file.name,
    fileSize: options.file.size,
    mimeType: options.file.type || 'application/octet-stream',
    businessType: options.businessType,
    businessPayload: options.businessPayload,
  })) as MultipartInitiateResponse;

  const ranges = createChunkRanges(options.file.size, initiate.partSize);
  const uploadUrlByPartNumber = await fetchPartUrls(
    initiate.sessionId,
    ranges.map((range) => range.partNumber)
  );

  const partProgress = new Map<number, number>();
  const completedParts: Array<{ partNumber: number; etag: string }> = [];
  let nextIndex = 0;

  const reportPartProgress = (range: ChunkRange, loaded: number) => {
    recordPartProgress(partProgress, range, loaded);
    options.onProgress?.(Math.min(99, calculateAggregateProgress(partProgress, options.file.size)));
  };

  const worker = async () => {
    while (nextIndex < ranges.length) {
      const range = ranges[nextIndex];
      nextIndex += 1;
      const uploadUrl = uploadUrlByPartNumber.get(range.partNumber);
      if (!uploadUrl) {
        throw new Error(`缺少分片上传地址: partNumber=${range.partNumber}`);
      }
      const etag = await uploadPartWithRetry({
        file: options.file,
        range,
        uploadUrl,
        signal: options.signal,
        onProgress: (loaded) => reportPartProgress(range, loaded),
        refreshUploadUrl: async () => {
          const refreshedUrls = await fetchPartUrls(initiate.sessionId, [range.partNumber]);
          const refreshedUrl = refreshedUrls.get(range.partNumber);
          if (!refreshedUrl) {
            throw new Error(`缺少刷新后的分片上传地址: partNumber=${range.partNumber}`);
          }
          uploadUrlByPartNumber.set(range.partNumber, refreshedUrl);
          return refreshedUrl;
        },
      });
      reportPartProgress(range, range.end - range.start);
      completedParts.push({ partNumber: range.partNumber, etag });
    }
  };

  try {
    const concurrency = Math.min(Math.max(1, config.maxConcurrency), ranges.length);
    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    completedParts.sort((a, b) => a.partNumber - b.partNumber);
    options.onProgress?.(100);
    const completed = (await apiClient.post(
      `/uploads/multipart/${initiate.sessionId}/complete`,
      { parts: completedParts },
      { timeout: COMPLETE_UPLOAD_TIMEOUT_MS }
    )) as MultipartCompleteResponse<T>;
    return completed.result;
  } catch (error) {
    await apiClient.post(`/uploads/multipart/${initiate.sessionId}/abort`).catch(() => undefined);
    throw error;
  }
}

async function fetchPartUrls(
  sessionId: string,
  partNumbers: number[]
): Promise<Map<number, string>> {
  const uploadUrlByPartNumber = new Map<number, string>();
  for (const batch of chunkPartNumbers(partNumbers)) {
    const partUrls = (await apiClient.post(`/uploads/multipart/${sessionId}/parts`, {
      partNumbers: batch,
    })) as MultipartPartUrlResponse;
    partUrls.parts.forEach((part) => {
      uploadUrlByPartNumber.set(part.partNumber, part.uploadUrl);
    });
  }
  return uploadUrlByPartNumber;
}

async function uploadPartWithRetry({
  file,
  range,
  uploadUrl,
  signal,
  onProgress,
  refreshUploadUrl,
}: {
  file: File;
  range: ChunkRange;
  uploadUrl: string;
  signal?: AbortSignal;
  onProgress: (loaded: number) => void;
  refreshUploadUrl: () => Promise<string>;
}): Promise<string> {
  let lastError: unknown;
  let currentUploadUrl = uploadUrl;
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      return await uploadPart({ file, range, uploadUrl: currentUploadUrl, signal, onProgress });
    } catch (error) {
      lastError = error;
      if (signal?.aborted) break;
      if (isHttpStatusError(error, 403)) {
        currentUploadUrl = await refreshUploadUrl();
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error('分片上传失败');
}

function isHttpStatusError(error: unknown, status: number): boolean {
  return (
    error instanceof Error && 'status' in error && (error as { status?: number }).status === status
  );
}

function uploadPart({
  file,
  range,
  uploadUrl,
  signal,
  onProgress,
}: {
  file: File;
  range: ChunkRange;
  uploadUrl: string;
  signal?: AbortSignal;
  onProgress: (loaded: number) => void;
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => {
      xhr.abort();
      reject(new DOMException('上传已取消', 'AbortError'));
    };
    if (signal?.aborted) {
      abort();
      return;
    }
    signal?.addEventListener('abort', abort, { once: true });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    };
    xhr.onload = () => {
      signal?.removeEventListener('abort', abort);
      if (xhr.status < 200 || xhr.status >= 300) {
        const error = new Error(`分片上传失败: HTTP ${xhr.status}`) as Error & { status?: number };
        error.status = xhr.status;
        reject(error);
        return;
      }
      const etag = xhr.getResponseHeader('ETag');
      if (!etag) {
        reject(new Error('对象存储未返回 ETag，请检查 CORS 暴露配置'));
        return;
      }
      resolve(etag);
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', abort);
      reject(new Error('分片上传网络错误'));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', abort);
      reject(new DOMException('上传已取消', 'AbortError'));
    };
    xhr.open('PUT', uploadUrl);
    xhr.send(file.slice(range.start, range.end));
  });
}
